/**
 * Payment Tracker Component
 * Track and manage payments for golf outings.
 */
import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Banknote, Smartphone, Check, AlertCircle, Send, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { useOuting } from '../context/OutingContext';
import { PAYMENT_STATUS, PAYMENT_METHODS, formatCurrency, calculatePaymentSummary } from '../utils/outingHelpers';

export default function PaymentTracker({ outingId, user, onRefresh }) {
  const api = useOutingApi();
  const { teams, payments, fetchPayments, fetchTeams } = useOuting();
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [recording, setRecording] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchPayments(outingId);
    fetchTeams(outingId);
  }, [outingId, fetchPayments, fetchTeams]);

  const summary = calculatePaymentSummary(teams, payments);

  const handleRecordPayment = async () => {
    if (!selectedTeam || !newPayment.amount) {
      toast.error('Please select a team and enter amount');
      return;
    }
    
    setRecording(true);
    try {
      await api.recordPayment(outingId, {
        team_id: selectedTeam.id,
        amount: parseFloat(newPayment.amount),
        method: newPayment.method,
        notes: newPayment.notes
      }, user.user_id, user.display_name || 'Organizer');
      
      toast.success('Payment recorded!');
      setShowRecordModal(false);
      setSelectedTeam(null);
      setNewPayment({ amount: '', method: 'cash', notes: '' });
      fetchPayments(outingId);
      fetchTeams(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRecording(false);
    }
  };

  const openRecordPayment = (team) => {
    setSelectedTeam(team);
    setNewPayment({
      amount: (team.total_due - team.total_paid).toString(),
      method: 'cash',
      notes: ''
    });
    setShowRecordModal(true);
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'zelle': return <Smartphone className="w-4 h-4" />;
      case 'venmo': return <Smartphone className="w-4 h-4" />;
      case 'card': return <CreditCard className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-zinc-400" />
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalDue)}</p>
            <p className="text-xs text-zinc-400">Total Due</p>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-green-500/30">
          <CardContent className="p-4 text-center">
            <Check className="w-6 h-6 mx-auto mb-2 text-green-400" />
            <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.totalPaid)}</p>
            <p className="text-xs text-zinc-400">Collected</p>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-red-500/30">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
            <p className="text-2xl font-bold text-red-400">{formatCurrency(summary.outstanding)}</p>
            <p className="text-xs text-zinc-400">Outstanding</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-4">
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-zinc-400">Collection Progress</span>
            <span className="text-white">{Math.round(summary.paidPercentage)}%</span>
          </div>
          <Progress value={summary.paidPercentage} className="h-3" />
        </CardContent>
      </Card>

      {/* Teams Payment Status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Teams</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchPayments(outingId);
            fetchTeams(outingId);
          }}
          className="border-white/20"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {teams.length === 0 ? (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-8 text-center">
            <p className="text-zinc-400">No teams to track payments for</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {teams.map(team => {
            const statusConfig = PAYMENT_STATUS[team.payment_status] || PAYMENT_STATUS.pending;
            const remaining = team.total_due - team.total_paid;
            
            return (
              <Card key={team.id} className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-white">{team.name}</h4>
                        <Badge className={`${statusConfig.bgColor} text-${statusConfig.color}-400`}>
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-400">
                        {team.captain_name} • {team.members?.length || 0} players
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-green-400">
                          {formatCurrency(team.total_paid)} paid
                        </span>
                        {remaining > 0 && (
                          <span className="text-red-400">
                            {formatCurrency(remaining)} owed
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {team.payment_status !== 'paid' && (
                      <Button
                        size="sm"
                        onClick={() => openRecordPayment(team)}
                        className="bg-green-600 hover:bg-green-500"
                        data-testid={`record-payment-${team.id}`}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Record
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent Payments */}
      {payments.length > 0 && (
        <>
          <h3 className="text-lg font-bold text-white mt-6">Recent Payments</h3>
          <div className="space-y-2">
            {payments.slice(0, 10).map(payment => (
              <Card key={payment.id} className="bg-[#121212] border-white/10">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      {getMethodIcon(payment.method)}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{payment.player_name}</p>
                      <p className="text-xs text-zinc-400">
                        {new Date(payment.recorded_at).toLocaleDateString()} via {payment.method}
                      </p>
                    </div>
                  </div>
                  <span className="text-green-400 font-semibold">
                    +{formatCurrency(payment.amount)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Record Payment Modal */}
      <Dialog open={showRecordModal} onOpenChange={setShowRecordModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {selectedTeam && (
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="font-semibold">{selectedTeam.name}</p>
                <p className="text-sm text-zinc-400">
                  Owed: {formatCurrency(selectedTeam.total_due - selectedTeam.total_paid)}
                </p>
              </div>
            )}
            
            <div>
              <Label>Amount *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/20 pl-9"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <Select
                value={newPayment.method}
                onValueChange={(v) => setNewPayment({ ...newPayment, method: v })}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/20 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/20">
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      <span className="flex items-center gap-2">
                        {m.icon} {m.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={newPayment.notes}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                placeholder="e.g., Check #1234"
                className="bg-[#1A1A1A] border-white/20 mt-1"
              />
            </div>
            
            <Button
              onClick={handleRecordPayment}
              disabled={recording}
              className="w-full bg-green-600 hover:bg-green-500"
              data-testid="submit-payment-btn"
            >
              {recording ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
